import { Component, ReactNode, ErrorInfo, ReactElement } from 'react';
import { MessageBar, MessageBarBody, MessageBarTitle, Button, makeStyles, tokens } from '@fluentui/react-components';
import { ErrorCircle24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    padding: tokens.spacingHorizontalXXL,
    gap: tokens.spacingVerticalM,
  },
  messageBar: {
    maxWidth: '600px',
    width: '100%',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
});

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback UI to render instead of default error message */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error boundary component to catch and display React component errors gracefully.
 * Prevents entire app from crashing when a component throws.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    
    this.setState({ errorInfo });
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <ErrorBoundaryFallback 
        error={this.state.error} 
        onRefresh={this.handleRefresh} 
        onRetry={this.handleReset} 
      />;
    }

    return this.props.children;
  }
}

interface ErrorBoundaryFallbackProps {
  error?: Error;
  onRefresh: () => void;
  onRetry: () => void;
}

/**
 * Default fallback UI for ErrorBoundary.
 * Extracted as functional component for styling hooks.
 */
function ErrorBoundaryFallback({ error, onRefresh, onRetry }: ErrorBoundaryFallbackProps): ReactElement {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <MessageBar intent="error" icon={<ErrorCircle24Regular />} className={styles.messageBar}>
        <MessageBarBody>
          <MessageBarTitle>Something went wrong</MessageBarTitle>
          {error?.message && (
            <div>Error: {error.message}</div>
          )}
          Please try refreshing the page. If the problem persists, contact support.
        </MessageBarBody>
      </MessageBar>
      <div className={styles.actions}>
        <Button appearance="primary" onClick={onRefresh}>
          Refresh Page
        </Button>
        <Button appearance="secondary" onClick={onRetry}>
          Try Again
        </Button>
      </div>
    </div>
  );
}

export default ErrorBoundary;
